/* eslint-disable no-undef */
import React from 'react'
import styled from 'styled-components'
import ParseContent from 'components/shared/ParseContent'

// Elements
import ButtonDefault from 'components/elements/ButtonDefault'
import Blogik, {
  BlogConsumer,
  BlogButton,
  BlogFilter,
} from 'components/shared/Blogik'

// Components
import Image from 'components/shared/ImageGatsby'
import { ScrollRevealItem } from 'components/shared/ScrollReveal'

// SVG
import ArrowRightWhite from 'img/arrow-right-white.svg'

const StyledImage = styled(Image)`
  @media (max-width: 767px) {
    max-height: 263px;
  }

  @media (min-width: 768px) {
    max-height: 357px;
  }

  @media (min-width: 1200px) {
    min-height: 488px;
    max-height: 488px;
  }
`
const ExcerptWrapper = styled.div`
  bottom: 100px;

  @media (max-width: 767px) {
    bottom: 0;
  }
`

const ExcerptContent = styled.div`
  min-height: 256px;
  background-color: ${(props) => props.theme.color.face.custom};
`

const Excerpt = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    font-size: ${(props) => props.theme.font.size.base};
    color: ${(props) => props.theme.color.text.secondary};
    line-height: ${(props) => props.theme.font.size[20]};
  }

  p {
    overflow: hidden;
    display: -webkit-box;
    font-weight: 300;
    -webkit-box-orient: vertical;

    @media (min-width: 1200px) {
      -webkit-line-clamp: 3;
    }

    @media (max-width: 1199px) {
      -webkit-line-clamp: 4;
    }
  }
`

const BlogWrapper = styled.div`
  @media (max-width: 767px) {
    border-bottom: 1px solid ${(props) => props.theme.color.text.secondary};
  }
`

interface PostsVacaturesProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Posts
}

function PostsVacatures({ fields }: PostsVacaturesProps) {
  return (
    <section>
      <div className="container py-lg-5 my-5">
        <div className="row justify-content-center">
          <div className="col-lg-9 col-12 d-flex flex-column">
            <Blogik
              postType="vacatures"
              settings={{
                id: 'blog',
                limit: Number(fields.limit),
              }}
            >
              <div>
                <VacatureFilters />
                <VacatureGrid fields={fields} />
              </div>
            </Blogik>
          </div>
        </div>
      </div>
    </section>
  )
}

const StyledBlogFilter = styled(BlogFilter)<{ selected: boolean }>`
  font-weight: ${(props) =>
    props.selected ? props.theme.font.weight.l : props.theme.font.weight.s};
`

interface VacatureFilterProps {
  node: {
    id: string
    name: string
  }
}

function VacatureFilters() {
  return (
    <BlogConsumer>
      {({ categories, isSelected }: any) => (
        <div className="container d-flex">
          {categories.map((category: VacatureFilterProps) => (
            <StyledBlogFilter
              key={category.node.id}
              id={category.node.id}
              selected={isSelected(category.node.id)}
              className="mr-3 d-none"
            >
              {category.node.name}
            </StyledBlogFilter>
          ))}
        </div>
      )}
    </BlogConsumer>
  )
}

interface VacatureGridProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Posts
}

function VacatureGrid({ fields }: VacatureGridProps) {
  return (
    <BlogConsumer>
      {({ posts, showMoreButton }: any) => (
        <div className="container">
          <div className="row">
            {posts.map((post: VacatureGridPostProps, postIndex: number) => (
              <ScrollRevealItem key={post.node.id} sequenceIndex={postIndex}>
                <VacatureGridPost node={post.node} />
              </ScrollRevealItem>
            ))}
          </div>

          {showMoreButton && (
            <div className="mt-5 text-center">
              <ButtonDefault to="/" isCustom>
                <BlogButton>{fields.loadMoreText}</BlogButton>
              </ButtonDefault>
            </div>
          )}
        </div>
      )}
    </BlogConsumer>
  )
}

interface VacatureGridPostProps {
  node: {
    id: string
    title: string
    uri: string
    vacancyDetail: {
      excerpt: string | null
      thumbnail: GatsbyTypes.Maybe<GatsbyTypes.WpMediaItem>
    }
  }
}

function VacatureGridPost({ node }: VacatureGridPostProps) {
  return (
    <BlogWrapper>
      <StyledImage image={node.vacancyDetail.thumbnail} loading="lazy" />
      <div className="d-flex justify-content-center">
        <ExcerptWrapper className="row position-relative align-items-center justify-content-between mx-lg-5 mx-0">
          <ExcerptContent className="p-lg-5 p-4 col-md-10 col-12">
            <Excerpt content={node.vacancyDetail.excerpt} />
            <ButtonDefault to={node.uri} className="mt-4">
              bekijk vacature
            </ButtonDefault>
          </ExcerptContent>
        </ExcerptWrapper>
      </div>
    </BlogWrapper>
  )
}

export default PostsVacatures
